import { Item, RenderItemFormSidebarPanelCtx } from "datocms-plugin-sdk";
import { Button, Canvas } from "datocms-react-ui";
import { sendMessageToChatGPT } from "../utils/chatgpt";
import { useState } from "react";
import StoryGeneratorProgress, { StoryGenerationProgress } from "../components/StoryGeneratorProgress";
import { LogLevel, buildClient } from '@datocms/cma-client-browser';
import {Dast, createEnumerationDast, createParagraphs, createSimpleTextDast, createSimpleTitleDast} from "../utils/Dast";
import { StoryCardChatGPTAnswer, LocalizedDatoImage, LocalizedString, LocalizedStructuredText, StoryCardData, StoryData, TextCard, EnumerationCard } from "../utils/common-types";
import { arrayToObject } from "../utils/arrayToObject";
import React from "react";

async function insertStoryCard(apiToken: string, storyCardData: StoryCardData): Promise<string> {
  const client = buildClient({ apiToken, logLevel: LogLevel.BASIC, });
  let newStoryCardId : string;
  try {
    const newStoryCard = await client.items.create({
      item_type: {
        type: "item_type",
        id: "LbNWM4RGQGW9g232h87p1Q"
      },
      text_before_image: storyCardData.textBeforeImage,
      image: null, // Since the image must be null
      text_after_image: storyCardData.textAfterImage,
      background_image: {
        upload_id: storyCardData.backgroundImageId,
      }
    });
    newStoryCardId = newStoryCard.id;
    return newStoryCardId;
  } catch (error) {
    console.error('Error creating story card:', error);
    throw error;
  }
}

async function insertStory(apiToken : string, storyData : StoryData) : Promise<string> {
  const client = buildClient({ apiToken, logLevel: LogLevel.BASIC, });
  let newStoryId : string;
  try {
    const newStory = await client.items.create({
      item_type: {
        type: "item_type",
        id: "xo3ARDQ2QYiEMsfqWojbTg"
      },
      card_image: {
        upload_id: storyData.cardImageId
      },
      card_text: storyData.cardText,
      title_image: {
        upload_id: storyData.titleImageId,
      },
      title: storyData.titleText,
      story_cards: storyData.storyCards,
      button_text: storyData.buttonText,
      button_action: JSON.stringify(storyData.buttonAction),
      tags: storyData.tags,
      original_article: storyData.originalArticle
    });
    newStoryId = newStory.id;
    return newStoryId;
  } catch (error) {
    console.error('Error creating story:', error);
    throw error;
  }
}

function getDefaultTitleImageIdAndBackgroundColor() {
  const possibleTitleImageIds = [
    "cvIgNO5YSZi9r0qpLOj97Q", //#fce5ce
    "QcaFdaZfTaWpAasEuECPBA", //#ff7f66
    "NTim8SUhSiW-xLkELp_xjg", //#fcbc9c
    "W8f3Oi-bQjiGCWkjPFED3g", //#366363
    "nS064UDzRd2lmveRgiogtQ",
    "ONMG5shfRCayqIIZGkNIuA", //#71d3f4
  ];
  const rnd = Math.random();
  return possibleTitleImageIds[Math.floor(rnd*(possibleTitleImageIds.length))];
}

type FormValues = {
  title : LocalizedString;
  headline : LocalizedString;
  paragraph1 : LocalizedStructuredText;
  image1 : LocalizedDatoImage;
  paragraph2 : LocalizedStructuredText;
  paragraph3 : LocalizedStructuredText;
  paragraph4 : LocalizedStructuredText;
  tags : string;
};

type Card = TextCard | EnumerationCard;

async function getStoryCards(itemId : string, formValues: FormValues, language : string) {
  const title = formValues.title;
  const headline = formValues.headline;
  const paragraph1 = formValues.paragraph1;
  const image1 = formValues.image1;
  const paragraph2 = formValues.paragraph2;
  const paragraph3 = formValues.paragraph3;
  const paragraph4 = formValues.paragraph4;
  const tags = formValues.tags;

  const numberOfStoryCards = 3;
  const prompt = `You must convert a long article to a short version which will be readed on cards.
	You must generate ${numberOfStoryCards} cards. Every card must contain 4-9 sentences and must write about a specific topic mentioned in the article.
	You don't have to follow the article, but the generated short version is a teaser for it.

	Your answer must be a JSON in the following format:
	{
    "title": "A short title for the card. Optimally 3 words"
		"short": "A short summery about this 'story'. Must be maximum 10 words long. (Basically this is a long title)",
		"cards": [
      ${"      { card struct, see the struct  elow }\n".repeat(numberOfStoryCards)}
		]
	}

  The card strut can be a text card or an enumeration card:

  Text typed card:
    {
      "type": "text",
      "subtitle": "The subtitle of the card. This will be the first sentence formatted as heading. It must be brief and describe the topic of this card",
      "content": "4-7 sentences"
    }

  Enumeration typed card:
    {
      "type": "enumeration",
      "subtitle": "The subtitle of the card. This will be the first sentence formatted as heading. It must be brief and describe the topic of this card",
      "content": {
        "introduction": "A short describtion about the enumeration. Can be empty.",
        "bulletpoints": ["first bulletpoint", "second bulletpoint", "further bulletpoints, max 7"]
      }
    }


	Here is the full story: 

	Title: ${title[language]}
	Headline: ${headline[language]}
	Body: ${JSON.stringify(paragraph1[language]) + JSON.stringify(paragraph2[language]) + JSON.stringify(paragraph3[language]) + JSON.stringify(paragraph4[language])}

  Use the original article language.
	`;

  const answer = await sendMessageToChatGPT({ context: prompt, messages: [], jsonResponse: true }) as StoryCardChatGPTAnswer;
  return {cards: answer.cards, title: answer.title, short: answer.short};
}

async function createStoryCards(cards : {[key: string] : Card}[], backgroundImageId : string, apiToken: string) {
  const cardIds : string[] = [];   
  for (const localizedCardData of cards) {
    const textBeforeImage : {[key : string] : Dast } = {};
    const textAfterImage : {[key : string] : Dast } = {};

    for (const locale in localizedCardData) {
      const cardData = localizedCardData[locale];

      textBeforeImage[locale] = createSimpleTitleDast(cardData.subtitle);
      if (cardData.type === "text") {
        textAfterImage[locale] = createSimpleTextDast([cardData.content]);
      } else if (cardData.type === "enumeration") {
        textAfterImage[locale] = createEnumerationDast(cardData.content.bulletpoints);
        console.log({textAfterImage});
        textAfterImage[locale].document.children = [...createParagraphs([cardData.content.introduction]), ...textAfterImage[locale].document.children]
      }
    }
    const cardId = await insertStoryCard(apiToken, {
      textBeforeImage,
      textAfterImage,
      backgroundImageId
    });
    cardIds.push(cardId);    
  } 
  return cardIds; 
}

async function createStory(title: {[locale : string] : string}, short : {[locale : string] : string}, buttonText : {[locale : string] : string}, cardIds : string[], image1: any, backgroundImageId : string, tags : string, itemId : string, apiToken : string) {
  const story : StoryData = {
    titleText: short,
    cardText: title,
    cardImageId: image1.en?.upload_id ?? backgroundImageId,
    titleImageId: backgroundImageId,
    storyCards: cardIds,
    buttonText,
    buttonAction: {
      type: "openUrl",
      value: `https://perisyncarticles.com/?locale=en&id=${itemId}`
    },
    tags,
    originalArticle: itemId
  }
  return await insertStory(apiToken, story);
}

async function generateStoryCardsFromArticle(
  itemId : string,
  formValues: FormValues,
  locales : string[],
  setLocaleProgress : React.Dispatch<React.SetStateAction<{ [key: string]: boolean; }>>,
  currentUserAccessToken : string
) {
  const numberOfStoryCards = 3;


  const image1 = formValues.image1;
  const tags = formValues.tags;
  const backgroundImageId = getDefaultTitleImageIdAndBackgroundColor();

  const storyCardProgressPromises : Promise<{cards: (TextCard | EnumerationCard)[], short : string, title: string}>[] = [];
  const storyCards : {[key : string] : (TextCard | EnumerationCard)[]} = {};
  const title : {[key : string] : string} = {};
  const short : {[key : string] : string} = {};
  for (const locale of locales) {
    const storyCardPromise = getStoryCards(itemId, formValues, locale);
    storyCardPromise.then((res) => {
      setLocaleProgress((localeProgress) => {
        const newLocaleProgress = {...localeProgress};
        newLocaleProgress[locale] = true;
        return newLocaleProgress;
      });
      storyCards[locale] = res.cards;
      title[locale] = res.title;
      short[locale] = res.short;
    });
    storyCardProgressPromises.push(storyCardPromise);
  }
  await Promise.all(storyCardProgressPromises);

  console.log({storyCards})
  const localizedStoryCards : {[key: string]: Card}[] = [];
  for (let i=0; i<numberOfStoryCards; ++i) {
    localizedStoryCards[i] = {};
  }

  const wrongLocales : string[] = [];
  for (const locale in storyCards) {
    const storyCardsInCurrentLanguage = storyCards[locale];
    if (storyCardsInCurrentLanguage.length == numberOfStoryCards) {
      for (let i=0; i<numberOfStoryCards; ++i) {
        localizedStoryCards[i][locale] = storyCardsInCurrentLanguage[i];
      }
    } else {
      wrongLocales.push(locale);
    }
  }
  if (wrongLocales.length > 0) {
    alert(`The generation for the following locales was not successful: ${wrongLocales.join(", ")}`);
    for (const wrongLocale of wrongLocales) {
      delete title[wrongLocale];
      delete short[wrongLocale];
    }
  }

  // Todo : I forgot to localize the button text and button action.
  const buttonText : {[key : string] : string} = {};
  for (const locale of locales) buttonText[locale] = "More";
  // localizedStoryCards has the same content as storyCards, but the locale key and the number are switched
  const cardIds = await createStoryCards(localizedStoryCards, backgroundImageId, currentUserAccessToken);
  const storyId = await createStory(
    title, 
    short,
    buttonText,
    cardIds,
    image1,
    backgroundImageId,
    tags,
    itemId,
    currentUserAccessToken
  )

  if (window.top === null) {
    alert("I can't redirect you to the created story, the top object does not exists")
  } else {
    window.top.location.href=`https://cycle-tracker-backend.admin.datocms.com/editor/item_types/xo3ARDQ2QYiEMsfqWojbTg/items/${storyId}`;
  }
}

type Props = {
  ctx: RenderItemFormSidebarPanelCtx,
};

function ArticleToStorySidePanel({ ctx }: Props) {
  const [progress, setProgress] = useState<boolean>(false);
  const locales = Object.keys(ctx.formValues!.title!).filter((key : string) => (ctx as any).formValues!.title![key] != null);
  const [localeProgress, setLocaleProgress] = useState<{[key: string] : boolean}>(arrayToObject(locales));
  const [storyCardsCreated, setStoryCardsCreated] = useState(false);


  const itemId = ctx.item!.id;

  const creatingStoryCardLabelStyle : React.CSSProperties = {
    visibility: (Object.values(localeProgress).every(v=>v))?"visible":"hidden",
    color: (storyCardsCreated)?"green":"orange"
  };

  return (
    <Canvas ctx={ctx}>
      <div className="input-label">Story generator V2.0</div>
      <div>
        {!progress && 
          <Button onClick={() => {
            generateStoryCardsFromArticle(
              itemId, 
              ctx.formValues as FormValues, 
              locales,
              setLocaleProgress,
              ctx.currentUserAccessToken!
            ).then(() => setProgress(false))
             .catch(() => setProgress(false));
            setProgress(true);
          }}>Generate story</Button>  
        }
        {progress && <span>Generation under progress</span>}
      </div>
      <div className="progress">
        <div>Locales</div>
        <ul>
          {locales.map((locale) => <li style={{color: (localeProgress[locale]?"green":"red")}}>{locale}</li>)}
        </ul>
        <div style={creatingStoryCardLabelStyle}>Creating story cards</div>
        <div style={{visibility: (storyCardsCreated)?"visible":"hidden"}}>Wait for redirection</div>
      </div>
    </Canvas>
  );
};

export default ArticleToStorySidePanel;

